import React from 'react';
import PropTypes from 'prop-types';

import {
  Autocomplete,
  TextField,
  CircularProgress,
  Stack,
  Avatar,
  Typography,
  Chip,
} from '@mui/material';
import {LocationOn, Translate as TranslateIcon} from '@mui/icons-material';

import backend from 'utils/backend';


const MultiCaregiverAutocomplete = (props) => {
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fetchNewData, setFetchNewData] = React.useState(true);

  const {selectedCaregiver, setSelectedCaregiver, label, multiple = false, value = [], onChange} = props;

  React.useEffect(() => {
    if (!multiple && selectedCaregiver) {
      setInputValue(selectedCaregiver.caregiverName);
    }
  }, []);

  React.useEffect(() => {
    if (loading) {
      return;
    } else if (fetchNewData ) {
      const fetchData = async () => {
        const localMatches = options.filter((option) =>
          option.caregiverName.toLowerCase().includes(inputValue.toLowerCase()),
        );

        // If less than 3 local matches, fetch new data
        if (localMatches.length < 3) {
          setLoading(true);

          try {
            const filter = [];
            if (inputValue) {
              filter.push({key: 'caregiverName', value: `${inputValue.trim()}*`});
            }
            filter.push({key: 'status', value: `ACTIVE`});
            const sortBy = {sortKey: 'createdAt', sortDirection: 'DESC'};

            const result = await backend.caregiver.queryCaregiver(filter, sortBy);

            const list = result.list;
            setOptions(list);
          } catch (e) {
            console.error('Failed to query caregiver data');
          } finally {
            setLoading(false);
          }
        }
      };
      fetchData();
    }
  }, [inputValue]);

  const handleInputChange = (event, newValue, reason) => {
    if (reason === 'input') {
      setFetchNewData(true);
    } else {
      setFetchNewData(false);
    }
    setInputValue(newValue); // Always update the inputValue for display
  };


  const handleOptionSelect = (event, selectedOption) => {
    if (multiple) {
      if (onChange) onChange(event, selectedOption);
    } else {
      if (selectedOption) {
        setSelectedCaregiver(selectedOption);
      }
    }
  };

  return (
    <Autocomplete
      freeSolo
      multiple={multiple}
      options={options}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onChange={handleOptionSelect}
      getOptionLabel={(option) => option.caregiverName || ''}
      loading={loading}
      value={multiple ? value : undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant="outlined"
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps?.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
      renderOption={(props, option) => {
        const caregiverName = option.caregiverName;
        const profileImg = option.profileImg;

        // Extract location from bio.questions
        const city = option.bio?.questions?.city?.questionData?.value || '';
        const province = option.bio?.questions?.province?.questionData?.value || '';
        const locationText = [city, province].filter(Boolean).join(', ');

        // Extract languages from bio.questions
        const languagesData = option.bio?.questions?.languages?.questionData?.value || [];
        const languages = languagesData.map((lang) => lang.name || lang).filter(Boolean);

        return (
          <li {...props} key={option.caregiverRn}>
            <Stack direction="row" spacing={{xs: 1.25, sm: 1.75}} alignItems="center" sx={{width: '100%', py: 1}}>
              <Avatar
                alt={caregiverName}
                src={profileImg}
                sx={{
                  width: {xs: 48, sm: 56},
                  height: {xs: 48, sm: 56},
                  border: '2px solid white',
                  boxShadow: '0 0 0 2px rgba(0,0,0,0.05)',
                }}
              />

              <Stack spacing={0.25} flex={1} minWidth={0}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{fontWeight: 700, lineHeight: 1.25, fontSize: {xs: '1rem', sm: '1.05rem'}}}
                  noWrap={false}
                >
                  {caregiverName}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  {
                    !!locationText &&
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{flexWrap: 'wrap'}}>
                    <LocationOn sx={{fontSize: 16, color: 'text.secondary'}} />
                    <Typography variant="body2" color="text.secondary">
                      {locationText}
                    </Typography>
                  </Stack>
                  }
                  {
                    !!languages.length &&
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{flexWrap: 'wrap'}}
                  >
                    <TranslateIcon sx={{fontSize: 16, color: 'text.secondary'}} />
                    {
                      languages.map((l, idx) => (
                        <Chip
                          key={idx}
                          label={`${l}`}
                          size="small"
                          sx={{
                            borderRadius: '16px',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            fontWeight: 500,
                            maxWidth: {xs: '100%', sm: 'none'},
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        />
                      ))
                    }
                  </Stack>
                  }
                </Stack>
              </Stack>
            </Stack>
          </li>
        );
      }}
    />
  );
};

MultiCaregiverAutocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  selectedCaregiver: PropTypes.object,
  setSelectedCaregiver: PropTypes.func,
  multiple: PropTypes.bool,
  value: PropTypes.array,
  onChange: PropTypes.func,
};

export default MultiCaregiverAutocomplete;
